import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function SpotVancouversMarineLife() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "spot-vancouvers-marine-life-nearby";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "whytecliff-park": "Whytecliff Park",
    "lighthouse-park": "Lighthouse Park",
    "stanley-park-third-beach": "Stanley Park’s Third Beach",
    "deep-cove": "Deep Cove",
    "acadia-beach": "Acadia Beach",
    "whale-watching-tours": "Whale Watching Tours",
    "porteau-cove": "Porteau Cove Provincial Park",
    faq: "FAQ",
    "local-regulations": "Local Regulations",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  // IMPORTANT: You can adjust or expand references as needed
  const referencesData = [
    {
      url: "https://www.pac.dfo-mpo.gc.ca/fm-gp/species-especes/mammals-mammiferes/view-observer-eng.html",
      label: "Fisheries and Oceans Canada: Marine Mammal Regulations"
    },
    {
      url: "https://www.destinationvancouver.com/",
      label: "Destination Vancouver"
    },
    {
      url: "https://seasmartschool.com/",
      label: "Sea Smart - Marine Education"
    }
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Each location’s "id" matches the ID in the article sections
  const locations = [
    {
      id: "whytecliff-park",
      lat: 49.3740,
      lng: -123.2888,
      popupText: "Whytecliff Park (West Vancouver)"
    },
    {
      id: "lighthouse-park",
      lat: 49.3360,
      lng: -123.2620,
      popupText: "Lighthouse Park (West Vancouver)"
    },
    {
      id: "stanley-park-third-beach",
      lat: 49.3041,
      lng: -123.1567,
      popupText: "Stanley Park, Third Beach"
    },
    {
      id: "deep-cove",
      lat: 49.3269,
      lng: -122.9487,
      popupText: "Deep Cove (North Vancouver)"
    },
    {
      id: "acadia-beach",
      lat: 49.2671,
      lng: -123.2604,
      popupText: "Acadia Beach (UBC area)"
    },
    {
      id: "whale-watching-tours",
      lat: 49.1243,
      lng: -123.1865,
      popupText: "Whale-Watching Departure (Steveston)"
    },
    {
      id: "porteau-cove",
      lat: 49.5572,
      lng: -123.2359,
      popupText: "Porteau Cove Provincial Park"
    }
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Spot Vancouver's Marine Life Nearby</title>
        <meta
          name="description"
          content="Discover top locations to see marine life near Vancouver. From seals at Whytecliff Park to whale-watching tours, this guide covers where to go, what to see, regulations, costs, and more."
        />
        <meta
          name="keywords"
          content="Vancouver, marine life, whales, seals, starfish, tide pools, guide"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/spot-vancouvers-marine-life-nearby.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Spot Vancouver's Marine Life Nearby"
        />
        <meta
          property="og:description"
          content="A practical guide to discovering marine life around Vancouver, including orcas, harbor seals, tide pools, and more."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/spot-vancouvers-marine-life-nearby.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/spot-vancouvers-marine-life-nearby.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Spot Vancouver's Marine Life Nearby"
        />
        <meta
          name="twitter:description"
          content="Learn about the best places to see marine life in Vancouver, top tips, and local regulations for ethical wildlife viewing."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/spot-vancouvers-marine-life-nearby.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={ref_data?.image || "https://bcouver.com/assets/images/article-fallback.jpg"}
              alt={ref_data?.["image-desc"] || "Vancouver Marine Life"}
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver’s unique location on the Pacific Ocean offers a wealth
              of marine life experiences right at its doorstep. From tide pools
              filled with colorful sea stars to pods of orcas cruising through
              local waters, the region’s biodiversity makes it a prime spot for
              wildlife enthusiasts. This guide will help you discover top
              viewing locations, best times to visit, approximate costs,
              recommended activities, and essential regulations to ensure a
              safe, respectful, and memorable encounter with Vancouver’s marine
              creatures.
            </p>

            {/* SECTION: Whytecliff Park */}
            <h2 id="whytecliff-park">Whytecliff Park (West Vancouver)</h2>
            <p>
              <strong>Whytecliff Park</strong> is a scenic oceanside destination
              recognized as Canada’s first Marine Protected Area. Its rugged
              shoreline, pebble beach, and small island (accessible at low tide)
              boast more than 200 marine species. Divers, snorkelers, and
              shore-based explorers can encounter an impressive range of sea
              creatures here.
            </p>
            <p>
              <strong>Marine Life Highlights:</strong> Colorful invertebrates
              like rockfish, lingcod, sculpins, sea stars, and anemones. Keep an
              eye out for harbor seals and sea lions in summer.
            </p>
            <p>
              <strong>Activities:</strong> Shore diving, snorkeling, tide
              pooling, and scenic photography. Guided dive tours are available
              from local operators.
            </p>
            <p>
              <strong>Costs:</strong> Free to enter. Gear rental or guided tours
              range from C$75–$150. Parking is also free.
            </p>
            <p
              ref={(el) => setSpotRef("whytecliff-park", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates:</strong> (Latitude: 49.3740, Longitude:
              -123.2888){" "}
              <Marker
                coordY={49.3740}
                coordX={-123.2888}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>

            {/* SECTION: Lighthouse Park */}
            <h2 id="lighthouse-park">Lighthouse Park (West Vancouver)</h2>
            <p>
              <strong>Lighthouse Park</strong> showcases an old-growth forest,
              the historic Point Atkinson Lighthouse, and remarkable coastal
              scenery. The shoreline’s tide pools offer a quieter setting for
              spotting intertidal species, while rocky bluffs provide
              outstanding ocean views.
            </p>
            <p>
              <strong>Marine Life Highlights:</strong> Tide pools with sea
              stars, crabs, sea anemones, and urchins. Harbor seals often swim
              nearby. Birdwatchers can glimpse cormorants and bald eagles.
            </p>
            <p>
              <strong>Activities:</strong> Hiking forested trails, tide pooling,
              photography. No on-site rentals, so bring your own gear if
              kayaking nearby.
            </p>
            <p>
              <strong>Costs:</strong> Free parking and entry. Arrive early on
              weekends to secure a spot.
            </p>
            <p
              ref={(el) => setSpotRef("lighthouse-park", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates:</strong> (Latitude: 49.3360, Longitude:
              -123.2620){" "}
              <Marker
                coordY={49.3360}
                coordX={-123.2620}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>

            {/* SECTION: Stanley Park – Third Beach */}
            <h2 id="stanley-park-third-beach">Stanley Park’s Third Beach</h2>
            <p>
              A short ride from downtown, <strong>Stanley Park</strong> is
              renowned for its lush forests and sweeping waterfront vistas.
              Third Beach, on the park’s western edge, combines sandy shores
              with rocky areas ideal for tide pooling, all within the city
              limits.
            </p>
            <p>
              <strong>Marine Life Highlights:</strong> Small shore crabs,
              barnacles, sea stars, mussels, and occasionally harbor seals
              visible just offshore. Bird enthusiasts might spot great blue
              herons and bald eagles overhead.
            </p>
            <p>
              <strong>Activities:</strong> Beach exploration, cycling or walking
              the seawall, tide pooling at low tide. The Vancouver Aquarium is
              nearby for a deeper understanding of local marine species.
            </p>
            <p>
              <strong>Costs:</strong> Free to explore beaches. Paid parking
              applies; C$3–$4 per hour or ~$14/day in summer.
            </p>
            <p
              ref={(el) => setSpotRef("stanley-park-third-beach", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates:</strong> (Latitude: 49.3041, Longitude:
              -123.1567){" "}
              <Marker
                coordY={49.3041}
                coordX={-123.1567}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>

            {/* SECTION: Deep Cove */}
            <h2 id="deep-cove">Deep Cove (North Vancouver)</h2>
            <p>
              <strong>Deep Cove</strong> offers calm waters, a charming village,
              and a scenic harbor at the eastern tip of North Vancouver. Popular
              for kayaking and paddleboarding, the area is also a lovely spot
              for a leisurely shore stroll or a picnic.
            </p>
            <p>
              <strong>Marine Life Highlights:</strong> Harbor seals are frequently
              seen, sometimes popping up near kayakers. Tide pools reveal sea
              stars and small crabs near the rocky shoreline.
            </p>
            <p>
              <strong>Activities:</strong> Kayak rentals, guided paddle tours,
              pier fishing, tide pooling, and scenic hikes (e.g., Quarry Rock).
              Look out for moon jellyfish in summer.
            </p>
            <p>
              <strong>Costs:</strong> Free to explore on foot. Kayak or SUP
              rentals cost ~C$50 for 2 hours. Guided tours ~C$70–$90.
            </p>
            <p
              ref={(el) => setSpotRef("deep-cove", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates:</strong> (Latitude: 49.3269, Longitude:
              -122.9487){" "}
              <Marker
                coordY={49.3269}
                coordX={-122.9487}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>

            {/* SECTION: Acadia Beach */}
            <h2 id="acadia-beach">Acadia Beach (UBC – Pacific Spirit Park)</h2>
            <p>
              <strong>Acadia Beach</strong> is tucked away on Vancouver’s west
              side, near the UBC campus. Known for rocky terrain and bountiful
              intertidal life, Acadia is a quieter alternative to nearby Spanish
              Banks.
            </p>
            <p>
              <strong>Marine Life Highlights:</strong> Tide pools teeming with
              sea stars, sea anemones, crabs, and barnacles. Eagles and herons
              are commonly seen overhead.
            </p>
            <p>
              <strong>Activities:</strong> Tide pooling, photography, beach
              walks. Bring sturdy footwear to navigate rocky sections.
            </p>
            <p>
              <strong>Costs:</strong> No fees to access the beach or park. Free
              parking is available at designated lots.
            </p>
            <p
              ref={(el) => setSpotRef("acadia-beach", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates:</strong> (Latitude: 49.2671, Longitude:
              -123.2604){" "}
              <Marker
                coordY={49.2671}
                coordX={-123.2604}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>

            {/* SECTION: Whale Watching Tours */}
            <h2 id="whale-watching-tours">Whale Watching Tours</h2>
            <p>
              For a chance to see orcas and humpback whales up close, join a{" "}
              <strong>whale-watching tour</strong> departing from Vancouver or
              Steveston (south of the city). Guided excursions travel through
              the Strait of Georgia and surrounding Salish Sea, areas known for
              rich marine biodiversity.
            </p>
            <p>
              <strong>Marine Life Highlights:</strong> Orcas, humpback whales,
              gray whales, plus sea lions, seals, porpoises, and seabirds.
            </p>
            <p>
              <strong>Activities:</strong> Boat tours lasting 3–5 hours, often
              with educational commentary and sightings guarantees.
            </p>
            <p>
              <strong>Costs:</strong> C$150–$220 per adult. Many operators offer
              a free second trip if whales are not sighted.
            </p>
            <p
              ref={(el) => setSpotRef("whale-watching-tours", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Main Departure Coordinates:</strong> (Latitude: 49.1243,
              Longitude: -123.1865){" "}
              <Marker
                coordY={49.1243}
                coordX={-123.1865}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>

            {/* SECTION: Porteau Cove Provincial Park */}
            <h2 id="porteau-cove">Porteau Cove Provincial Park</h2>
            <p>
              <strong>Porteau Cove</strong> lies along the Sea-to-Sky Highway,
              about 40 minutes north of Vancouver. Popular with divers (thanks
              to sunk ships and artificial reefs), it also features a pebble
              beach and campsites with stunning mountain and ocean views.
            </p>
            <p>
              <strong>Marine Life Highlights:</strong> Giant Pacific octopus,
              wolf eels, lingcod, rockfish, and occasionally harbor seals.
            </p>
            <p>
              <strong>Activities:</strong> Scuba diving, snorkeling, kayaking,
              camping, and nighttime bioluminescent plankton viewing.
            </p>
            <p>
              <strong>Costs:</strong> Park entry is free; gear rentals or guided
              dives can range from C$50–$200. Campsites ~C$20/night.
            </p>
            <p
              ref={(el) => setSpotRef("porteau-cove", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates:</strong> (Latitude: 49.5572, Longitude:
              -123.2359){" "}
              <Marker
                coordY={49.5572}
                coordX={-123.2359}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. What’s the best time to go whale watching in Vancouver?</h3>
              <p>
                The prime whale watching season runs from April through October,
                with May to September often cited as peak months. Both orcas and
                humpbacks frequent local waters during this time.
              </p>

              <h3>2. Can I see marine life without paying for tours?</h3>
              <p>
                Absolutely. You can explore free sites like Whytecliff Park,
                Lighthouse Park, or Stanley Park’s Third Beach to spot seals,
                sea stars, and other creatures from shore. A whale tour is the
                most reliable way to see large whales, but you can often see
                seals, intertidal critters, and seabirds at no cost.
              </p>

              <h3>3. Do I need a wetsuit for snorkeling?</h3>
              <p>
                Vancouver’s waters are cold year-round (about 10–15°C), so a
                wetsuit is highly recommended for prolonged swims or snorkeling.
                Most guided tours provide suits, and local dive shops rent gear
                if you plan a DIY outing.
              </p>
            </div>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations / Best Practices</h2>
            <p>
              • Maintain a respectful distance from marine mammals. Boats must
              stay at least 100m from whales, and 400m from orcas in certain
              critical areas. Kayakers have the same responsibility. <br/>
              • Do not feed or touch wildlife. Marine Mammal Regulations
              prohibit feeding seals or sea lions. <br/>
              • Observe tide-pool etiquette: replace rocks carefully, keep
              intertidal creatures submerged, and never remove marine life.{" "}
              <br/>
              • Many spots like Whytecliff Park are Marine Protected Areas or
              Rockfish Conservation Areas; fishing or harvesting is prohibited.
              <br/>
              • Use caution: check tide tables to avoid getting trapped, wear
              appropriate footwear on slippery rocks, and be aware of cold water
              temperatures if swimming.
            </p>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Whether you’re scanning for orcas on a whale-watching excursion or
              flipping rocks in a tide pool to find secretive crabs, Vancouver’s
              marine ecosystem offers countless ways to connect with nature.
              Follow local guidelines, respect wildlife habitats, and take only
              pictures while leaving only footprints. Enjoy your explorations of
              this diverse coastal region, and may your trip be filled with
              unforgettable marine encounters.
            </p>

            {/* BOTTOM COMPONENT: references, etc. */}
            <Bottom
              referencesData={referencesData}
              ref_data={ref_data}
            />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}